import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { WindowConfig, WindowManagerContextType } from './windowTypes';
import Window from './Window';
import './Window.scss';

const WindowManagerContext = createContext<WindowManagerContextType | undefined>(undefined);

export const useWindowManager = () => {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error('useWindowManager must be used within WindowManagerProvider');
  }
  return context;
};

let windowIdCounter = 0;
const generateWindowId = () => `window_${++windowIdCounter}`;

export const WindowManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [windows, setWindows] = useState<WindowConfig[]>([]);
  const [nextZIndex, setNextZIndex] = useState(1000);

  const openWindow = useCallback((config: Omit<WindowConfig, 'id' | 'zIndex'>) => {
    const id = generateWindowId();

    setWindows(prev => {
      let container = config.containerElement;

      if (!container) {
        const activeTabPane = document.querySelector('.tab-pane.show.active') as HTMLElement;
        container = activeTabPane || document.body;
      }

      if (container && getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }

      const newZIndex = nextZIndex;
      setNextZIndex(nextZIndex + 2);

      const newWindow: WindowConfig = {
        ...config,
        id,
        zIndex: newZIndex,
        size: config.size || { width: 800, height: 600 },
        containerElement: container
      };

      return [...prev, newWindow];
    });

    return id;
  }, [nextZIndex]);

  const closeWindow = useCallback((id: string, result?: any) => {
    setWindows(prev => {
      const window = prev.find(w => w.id === id);
      if (window?.onClose) {
        window.onClose(result);
      }
      return prev.filter(w => w.id !== id);
    });
  }, []);

  const focusWindow = useCallback((id: string) => {
    setWindows(prev => {
      const window = prev.find(w => w.id === id);
      if (!window) return prev;

      const maxZ = Math.max(...prev.map(w => w.zIndex));
      if (window.zIndex === maxZ) return prev;

      const newZIndex = nextZIndex;
      setNextZIndex(nextZIndex + 2);

      return prev.map(w =>
        w.id === id ? { ...w, zIndex: newZIndex } : w
      );
    });
  }, [nextZIndex]);

  const updateWindowData = useCallback((id: string, data: any, loading?: boolean) => {
    setWindows(prev => prev.map(w =>
      w.id === id ? { ...w, data, loading: loading !== undefined ? loading : false } : w
    ));
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && windows.length > 0) {
        closeWindow(windows[windows.length - 1].id);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [windows, closeWindow]);

  const value: WindowManagerContextType = {
    windows,
    openWindow,
    closeWindow,
    focusWindow,
    updateWindowData
  };

  return (
    <WindowManagerContext.Provider value={value}>
      {children}
    </WindowManagerContext.Provider>
  );
};

export const WindowRenderer: React.FC = () => {
  const { windows } = useWindowManager();
  return (
    <>
      {windows.map(window => (
        <Window key={window.id} config={window} />
      ))}
    </>
  );
};
